import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronRight } from 'lucide-react'

import { Reveal } from '@/components/generals/reveal'
import { Container, Eyebrow } from '@/components/generals/primitives'

export type Crumb = {
    name: ReactNode
    href: string
}

interface PageHeaderProps {
    eyebrow?: ReactNode
    title: ReactNode
    lead?: ReactNode
    crumbs?: Crumb[]
    meta?: ReactNode
    actions?: ReactNode
    size?: 'default' | 'large'
}

export const PageHeader: FC<PageHeaderProps> = ({
    eyebrow,
    title,
    lead,
    crumbs = [],
    meta,
    actions,
    size = 'default'
}): ReactNode => {
    return (
        <section className='relative overflow-hidden bg-ink text-paper'>
            <Container>
                <div className='relative py-14 lg:py-20'>
                    {crumbs.length ? (
                        <nav aria-label='Breadcrumb'>
                            <ol className='flex flex-wrap items-center gap-2 font-mono text-[0.62rem] uppercase tracking-label text-ink-muted'>
                                {crumbs.map((crumb, index) => (
                                    <li key={crumb.href} className='flex items-center gap-2'>
                                        {index > 0 ? (
                                            <ChevronRight aria-hidden='true' className='size-3' />
                                        ) : null}

                                        {index === crumbs.length - 1 ? (
                                            <span aria-current='page' className='text-paper/70'>
                                                {crumb.name}
                                            </span>
                                        ) : (
                                            <Link
                                                href={crumb.href}
                                                className='transition-colors hover:text-minium-tint'
                                            >
                                                {crumb.name}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        </nav>
                    ) : null}

                    <div className='mt-8 grid gap-10 lg:grid-cols-12 lg:gap-16'>
                        <div className='lg:col-span-7'>
                            {eyebrow ? (
                                <Reveal>
                                    <Eyebrow tone='paper'>{eyebrow}</Eyebrow>
                                </Reveal>
                            ) : null}

                            <Reveal delay={60}>
                                <h1
                                    className={
                                        size === 'large'
                                            ? 'mt-6 font-heading text-[clamp(2.5rem,7.5vw,5.5rem)] font-extrabold leading-display tracking-display'
                                            : 'mt-6 font-heading text-4xl font-extrabold leading-[1.03] tracking-display sm:text-5xl lg:text-[3.75rem]'
                                    }
                                >
                                    {title}
                                </h1>
                            </Reveal>

                            {actions ? (
                                <Reveal delay={180}>
                                    <div className='mt-9 flex flex-col gap-3 sm:flex-row'>{actions}</div>
                                </Reveal>
                            ) : null}
                        </div>

                        <div className='lg:col-span-4 lg:col-start-9 lg:self-end'>
                            {lead ? (
                                <Reveal delay={120}>
                                    <p className='text-lg leading-relaxed text-paper/80'>
                                        {lead}
                                    </p>
                                </Reveal>
                            ) : null}

                            {meta ? (
                                <Reveal delay={200}>
                                    <div className='mt-8'>{meta}</div>
                                </Reveal>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    )
}
